import React, { useRef, useCallback, useState } from 'react'

import { Label } from '@/atoms/Typography'
import BoxedImage from '@/molecules/BoxedImage'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Mousewheel, A11y, Navigation } from 'swiper'

import {
  StyledTabFilter,
  StyledTabFilterList,
  StyledTabFilterNavigation,
  StyledTabFilterNavigationButton,
  StyledTabFilterTitle,
} from './styles'
import Icon from '@/atoms/Icon'

const swiperProps = {
  spaceBetween: 8,
  slidesPerView: 'auto',
  navigation: {
    nextEl: '.slider-button-next',
    prevEl: '.slider-button-prev',
  },
}

const TabFilter = ({ items, type, actions, hideImg = true }) => {
  const [focusedIndex, setFocusedIndex] = useState(() => {
    return items.findIndex(item => item.current) || 0
  })
  const swiperRef = useRef(null)

  const handleKeyDown = useCallback((event, index) => {
    const { key } = event

    if (key === 'ArrowLeft' || key === 'ArrowRight') {
      event.preventDefault()

      let newIndex
      if (key === 'ArrowLeft') {
        newIndex = index > 0 ? index - 1 : items.length - 1
      } else {
        newIndex = index < items.length - 1 ? index + 1 : 0
      }

      setFocusedIndex(newIndex)

      const newTab = document.getElementById(`tab-${newIndex}`)

      if (newTab) {
        newTab.focus()
      }

      if (swiperRef.current) {
        swiperRef.current.slideTo(newIndex)
      }
    } else if (key === 'Enter' || key === ' ') {
      event.preventDefault()

      if (actions?.onClick) {
        actions.onClick(items[index])
      }
    }
  }, [items, actions])

  const handleClick = useCallback((props, index) => {
    setFocusedIndex(index)

    if (swiperRef.current) {
      swiperRef.current.slideTo(index)
    }

    if (actions && actions.onClick) {
      actions.onClick(props)
    }
  }, [actions])

  return (
    items &&
    !!items.length && (
      <StyledTabFilterList gap={8} type={type}>
        <Swiper
          {...swiperProps}
          modules={[Mousewheel, A11y, Navigation]}
          role="tablist"
          aria-label="Filtri di ricerca"
          onSwiper={(swiper) => {
            swiperRef.current = swiper
          }}
        >
          {items.map((props, i) => {
            const {
              title,
              image,
              href,
              current = false,
              noBorder = true,
              full = true,
              small = false,
            } = props

            const tabLabel = (title || props.name) ?
              `Filtra per: "${(title || props.name)}"`
              :
              `Tab ${i + 1}`
            const isActive = current
            const isFocused = i === focusedIndex

            return (
              <SwiperSlide
                key={`${i}_${!!title ? title : props.name}`}
                role="presentation"
                aria-roledescription="tab"
                aria-label={`${i + 1} di ${items.length}: ${title || 'Tab'}`}
              >
                <StyledTabFilter
                  key={title}
                  small={small}
                  current={current}
                  hideImg={hideImg}
                  role="tab"
                  aria-selected={isActive}
                  aria-label={tabLabel}
                  id={`tab-${i}`}
                  tabIndex={isFocused ? 0 : -1}
                  onKeyDown={(e) => handleKeyDown(e, i)}
                  onClick={() => handleClick(props, i)}
                >
                  <BoxedImage
                    aria-label={tabLabel}
                    noBorder={noBorder}
                    full={full}
                    image={image}
                    href={href}
                    imageProps={{
                      alt: image.alt || image.title || `Immagine di ${tabLabel}`,
                    }}
                  >
                    {title && (
                      <StyledTabFilterTitle>
                        <Label bold as="span" typo="buttonRegular">
                          {title}
                        </Label>
                      </StyledTabFilterTitle>
                    )}
                  </BoxedImage>
                </StyledTabFilter>
              </SwiperSlide>
            )
          })}

          <StyledTabFilterNavigation aria-label="Controlli slider">
            <StyledTabFilterNavigationButton
              className='slider-button-prev'
              aria-label="Vai al contenuto precedente"
              type="button"
            >
              <Icon name='chevron-left' aria-hidden='true' />
            </StyledTabFilterNavigationButton>
            <StyledTabFilterNavigationButton
              className='slider-button-next'
              aria-label="Vai al contenuto successivo"
              type="button"
            >
              <Icon name='chevron-right' aria-hidden='true' />
            </StyledTabFilterNavigationButton>
          </StyledTabFilterNavigation>
        </Swiper>
      </StyledTabFilterList>
    )
  )
}

export default TabFilter
